<template>
  <div class="emp-more-page">
    <!-- 앱 헤더 -->
    <div class="app-header">
      <div class="header-title">더보기</div>
    </div>

    <div class="page-content">
    <!-- 프로필 카드 -->
    <div class="profile-card" @click="navigateTo('/emp/myinfo')">
      <div class="profile-avatar">
        <v-icon size="36" color="white">mdi-account</v-icon>
      </div>
      <div class="profile-info">
        <div class="profile-name">{{ userName }}</div>
        <div class="profile-job">{{ userJob }} · {{ sisulName }}</div>
      </div>
      <v-icon color="#9ca3af" size="20">mdi-chevron-right</v-icon>
    </div>

    <!-- 메뉴 그룹 -->
    <div class="menu-group">
      <div class="group-title">메뉴</div>
      <div class="menu-list">
        <div class="menu-item" @click="navigateTo('/emp/notice')">
          <div class="menu-icon" style="background: #e3f2fd;">
            <v-icon size="20" color="#1976d2">mdi-bullhorn</v-icon>
          </div>
          <div class="menu-content">
            <span class="menu-label">공지사항</span>
            <span v-if="noticeCount > 0" class="menu-badge">{{ noticeCount }}</span>
          </div>
          <v-icon color="#d1d5db" size="18">mdi-chevron-right</v-icon>
        </div>
      </div>
    </div>

    <!-- 계정 그룹 -->
    <div class="menu-group">
      <div class="group-title">계정</div>
      <div class="menu-list">
        <div class="menu-item" @click="navigateTo('/emp/myinfo')">
          <div class="menu-icon" style="background: #f3e8ff;">
            <v-icon size="20" color="#7c3aed">mdi-account-circle</v-icon>
          </div>
          <div class="menu-content">
            <span class="menu-label">내 정보</span>
          </div>
          <v-icon color="#d1d5db" size="18">mdi-chevron-right</v-icon>
        </div>

        <div class="menu-item" @click="navigateTo('/emp/settings')">
          <div class="menu-icon" style="background: #f1f5f9;">
            <v-icon size="20" color="#64748b">mdi-cog</v-icon>
          </div>
          <div class="menu-content">
            <span class="menu-label">설정</span>
          </div>
          <v-icon color="#d1d5db" size="18">mdi-chevron-right</v-icon>
        </div>
      </div>
    </div>

    <!-- 메뉴 그룹 3 -->
    <div class="menu-group">
      <div class="menu-list">
        <div class="menu-item logout" @click="handleLogout">
          <div class="menu-icon" style="background: #fee2e2;">
            <v-icon size="20" color="#dc2626">mdi-logout</v-icon>
          </div>
          <div class="menu-content">
            <span class="menu-label logout-text">로그아웃</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 버전 정보 -->
    <div class="version-info">
      <span>버전 1.0.0</span>
    </div>
    </div>

    <!-- 로그아웃 확인 다이얼로그 -->
    <v-dialog v-model="showLogoutDialog" max-width="300" >
      <v-card class="logout-dialog">
        <div class="dialog-content">
          <v-icon size="48" color="#dc2626">mdi-logout</v-icon>
          <h3>로그아웃</h3>
          <p>정말 로그아웃 하시겠습니까?</p>
        </div>
        <div class="dialog-actions">
          <button class="cancel-btn" @click="showLogoutDialog = false">취소</button>
          <button class="confirm-btn" @click="confirmLogout">확인</button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';

const store = useStore();
const router = useRouter();

// 사원 정보
const pwaUser = computed(() => store.getters['pwa/pwaUser']);
const userName = computed(() => pwaUser.value?.display_name || pwaUser.value?.emp_name || '사원');
const userJob = computed(() => pwaUser.value?.emp_job || '-');
const sisulName = computed(() => pwaUser.value?.sisul_name || pwaUser.value?.m_sisul_name || '-');

// 공지사항 개수
const noticeCount = ref(0);

// 로그아웃 다이얼로그
const showLogoutDialog = ref(false);

// 공지사항 개수 로드
const loadNoticeCount = async () => {
  try {
    const response = await axios.get('/api/notice/list', {
      params: { page: 1, limit: 1 },
      withCredentials: true
    });
    
    if (response.data.success) {
      // 3일 이내 공지사항 개수 (NEW 표시된 것)
      const notices = response.data.notices || [];
      const threeDaysAgo = Date.now() - (3 * 24 * 60 * 60 * 1000);
      noticeCount.value = notices.filter((n: any) => new Date(n.nc_wdate).getTime() > threeDaysAgo).length;
    }
  } catch (error) {
    console.error('공지사항 로드 오류:', error);
  }
};

const navigateTo = (path: string) => {
  router.push(path);
};

const handleLogout = () => {
  showLogoutDialog.value = true;
};

const confirmLogout = async () => {
  showLogoutDialog.value = false;
  await store.dispatch('pwa/performPwaLogout');
  router.push('/pwa/login');
};

onMounted(() => {
  loadNoticeCount();
});
</script>

<style scoped lang="scss">
.emp-more-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 16px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 100;
  
  .header-title {
    font-size: 17px;
    font-weight: 600;
  }
}

.page-content {
  padding: 16px;
  padding-bottom: 80px;
}

.profile-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  border-radius: 16px;
  padding: 18px 16px;
  margin-bottom: 20px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(5, 150, 105, 0.25);
  
  &:active {
    transform: scale(0.98);
  }
  
  .profile-avatar {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .profile-info {
    flex: 1;
    
    .profile-name {
      font-size: 18px;
      font-weight: 700;
      color: white;
      margin-bottom: 2px;
    }
    
    .profile-job {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.85);
    }
  }
}

.menu-group {
  margin-bottom: 20px;
  
  .group-title {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    margin-bottom: 8px;
    padding-left: 4px;
  }
  
  .menu-list {
    background: white;
    border-radius: 14px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    overflow: hidden;
  }
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:active {
    background: #f9fafb;
  }
  
  .menu-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .menu-content {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    
    .menu-label {
      font-size: 15px;
      font-weight: 500;
      color: #1f2937;
    }
    
    .menu-badge {
      background: #dc2626;
      color: white;
      font-size: 11px;
      font-weight: 600;
      padding: 2px 7px;
      border-radius: 10px;
    }
  }
  
  &.logout {
    .logout-text {
      color: #dc2626;
    }
  }
}

.version-info {
  text-align: center;
  padding: 20px;
  color: #9ca3af;
  font-size: 12px;
}

.logout-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  
  .dialog-content {
    padding: 28px 20px;
    text-align: center;
    
    h3 {
      font-size: 18px;
      font-weight: 700;
      color: #1f2937;
      margin: 12px 0 6px;
    }
    
    p {
      font-size: 14px;
      color: #6b7280;
      margin: 0;
    }
  }
  
  .dialog-actions {
    display: flex;
    border-top: 1px solid #e5e7eb;
    
    button {
      flex: 1;
      padding: 14px;
      font-size: 15px;
      font-weight: 600;
      border: none;
      cursor: pointer;
      transition: background 0.2s;
      
      &.cancel-btn {
        background: white;
        color: #6b7280;
        border-right: 1px solid #e5e7eb;
        
        &:active {
          background: #f3f4f6;
        }
      }
      
      &.confirm-btn {
        background: white;
        color: #dc2626;
        
        &:active {
          background: #fef2f2;
        }
      }
    }
  }
}
</style>

